/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
} from '@angular/core';

@Directive({
  selector: '[appHeaderLogoClick]',
  standalone: true,
})
export class HeaderLogoClickDirective implements AfterViewInit, OnDestroy {
  @Output() onHeaderLogoClick = new EventEmitter<MouseEvent>();

  private clickListener?: (event: Event) => void;
  private mutationObserver?: MutationObserver;
  private currentLogoArea?: HTMLElement;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    // Wait for the shadow DOM to be ready - use longer timeout
    setTimeout(() => {
      this.setupMutationObserver();
      this.attachClickListenerToLogoArea();
    }, 0);
  }

  ngOnDestroy(): void {
    // Clean up the mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    // Clean up the click listener
    this.removeClickListener();
  }

  private removeClickListener(): void {
    if (this.clickListener && this.currentLogoArea) {
      this.currentLogoArea.removeEventListener('click', this.clickListener);
      this.currentLogoArea = undefined;
    }
  }

  private setupMutationObserver(): void {
    const hostElement = this.el.nativeElement.querySelector('goa-app-header');
    const shadowRoot = (hostElement as any)?.shadowRoot;

    if (!shadowRoot) {
      console.warn('HeaderLogoClickDirective: No shadow root found');
      return;
    }

    // Watch for changes in the shadow DOM
    this.mutationObserver = new MutationObserver(() => {
      this.attachClickListenerToLogoArea();
    });

    this.mutationObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
    });
  }

  private attachClickListenerToLogoArea(): void {
    const logoArea = this.findLogoArea();

    if (!logoArea) {
      console.warn(
        'HeaderLogoClickDirective: Could not find .header-logo-title-area element'
      );
      return;
    }

    // If we already have a listener on this element, don't add another
    if (this.currentLogoArea === logoArea) {
      return;
    }

    // Remove old listener if exists
    this.removeClickListener();

    this.attachClickListener(logoArea);
    this.currentLogoArea = logoArea;
  }

  private findLogoArea(): HTMLElement | null {
    const hostElement = this.el.nativeElement.querySelector('goa-app-header');

    // Try to access shadow root directly on the host element (goab-app-header)
    const shadowRoot = (hostElement as any).shadowRoot;
    if (shadowRoot) {
      const logoArea = shadowRoot.querySelector(
        'div > div > .header-logo-title-area'
      ) as HTMLElement | null;
      return logoArea;
    }

    // Fallback: search in light DOM
    const logoArea = hostElement?.shadowRoot?.querySelector(
      'div > div > .header-logo-title-area'
    ) as HTMLElement | null;
    return logoArea;
  }

  private attachClickListener(logoArea: HTMLElement): void {
    // Add pointer cursor style
    logoArea.style.cursor = 'pointer';

    this.clickListener = (event: Event) => {
      const mouseEvent = event as MouseEvent;

      // Prevent default navigation
      event.preventDefault();
      event.stopPropagation();

      this.onHeaderLogoClick.emit(mouseEvent);
    };

    logoArea.addEventListener('click', this.clickListener);
  }
}
